import { InitialConfigType } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { ContactMentionNode } from '../nodes/ContactMentionNode';
import { MatterReferenceNode } from '../nodes/MatterReferenceNode';
import { BillReferenceNode } from '../nodes/BillReferenceNode';
import { DocumentReferenceNode } from '../nodes/DocumentReferenceNode';
import { EventBlockNode } from '../nodes/EventBlockNode';
import { calendarLexicalTheme } from './calendarTheme';

/**
 * Lexical editor configuration for EdgeCalendar
 * Extends the existing ChatInput patterns with legal entity nodes
 */
export const calendarEditorConfig: InitialConfigType = {
  namespace: 'EdgeCalendarEditor',
  theme: calendarLexicalTheme,
  onError: (error: Error) => {
    console.error('EdgeCalendar Lexical Error:', error);
  },
  nodes: [
    // Rich-text nodes used by shared Lexical toolbar/plugins
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    // Code blocks with syntax highlighting (Prism-powered)
    CodeNode,
    CodeHighlightNode,
    // Horizontal rule (--- in markdown)
    HorizontalRuleNode,
    // Legal entity nodes for @mentions, #matters, $bills, /documents
    ContactMentionNode,
    MatterReferenceNode,
    BillReferenceNode,
    DocumentReferenceNode,
    // Special node for Interactive Agenda View
    EventBlockNode,
  ],
};

/**
 * Configuration for Interactive Daily Agenda editor
 * Single editor instance with embedded event nodes
 */
export const interactiveAgendaConfig: InitialConfigType = {
  namespace: 'EdgeCalendarAgenda',
  theme: {
    ...calendarLexicalTheme,
    // Special styling for agenda view
    root: 'agenda-editor-root',
    paragraph: 'agenda-paragraph',
  },
  onError: (error: Error) => {
    console.error('EdgeCalendar Agenda Lexical Error:', error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    CodeNode,
    CodeHighlightNode,
    HorizontalRuleNode,
    ContactMentionNode,
    MatterReferenceNode,
    BillReferenceNode,
    DocumentReferenceNode,
    EventBlockNode, // Critical for agenda view
  ],
  editable: true, // Allow editing between event blocks
};

/**
 * Configuration for event templates
 * Pre-built templates with legal-specific content
 */
export const eventTemplateConfig: InitialConfigType = {
  namespace: 'EdgeCalendarTemplate',
  theme: {
    ...calendarLexicalTheme,
    // Template-specific styling
    root: 'template-editor-root',
  },
  onError: (error: Error) => {
    console.error('EdgeCalendar Template Lexical Error:', error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    CodeNode,
    CodeHighlightNode,
    HorizontalRuleNode,
    ContactMentionNode,
    MatterReferenceNode,
    BillReferenceNode,
    DocumentReferenceNode,
  ],
};

/**
 * Read-only configuration for displaying event descriptions
 * Used in event popovers and details views
 */
export const readOnlyEventConfig: InitialConfigType = {
  namespace: 'EdgeCalendarReadOnly',
  theme: calendarLexicalTheme,
  onError: (error: Error) => {
    console.error('EdgeCalendar ReadOnly Lexical Error:', error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    CodeNode,
    CodeHighlightNode,
    HorizontalRuleNode,
    ContactMentionNode,
    MatterReferenceNode,
    BillReferenceNode,
    DocumentReferenceNode,
  ],
  editable: false, // Read-only for display
}; 
